/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: pages\dashboards\widgets\ServicesStatusWidget.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Tools module.
----------------------------------------------------------------*/


import { useState, useEffect, useMemo, useContext, useCallback } from 'react';
import { CheckCircle, XCircle, AlertTriangle, Settings } from 'lucide-react';
import { WidgetContext } from '../components/WidgetWrapper';

interface ServiceInfo {
    status: string;
    version: string;
}

interface ServicesData {
    [key: string]: ServiceInfo;
}

interface ServicesStatusWidgetProps {
    config?: {
        selectedServices?: string[];
        showAppName?: boolean;
    };
    onConfigChange?: (config: Record<string, any>) => void;
    isEditMode?: boolean;
    registerConfigHandler?: (handler: () => void) => void;
}

// Default widget preferences for card styling
const DEFAULT_CARD_COLOR = '#1e293b';
const DEFAULT_BORDER_COLOR = '#334155';

// Mapping from technical names to friendly names
const SERVICE_NAMES: Record<string, string> = {
    freeswitch: 'PBX Engine',
    postgresql: 'Database',
    nginx: 'Web Server',
    fail2ban: 'Fail2Ban',
    nftables: 'Firewall',
    openvpn: 'OpenVPN',
    redis: 'Cache',
    'monitoring-api': 'Monitor API',
    api: 'Admin API',
};

const getStatusColor = (status: string) => {
    switch (status) {
        case 'active':
            return 'text-green-500 bg-green-500/10';
        case 'inactive':
            return 'text-red-500 bg-red-500/10';
        default:
            return 'text-yellow-500 bg-yellow-500/10';
    }
};

const getStatusIcon = (status: string) => {
    switch (status) {
        case 'active':
            return CheckCircle;
        case 'inactive':
            return XCircle;
        default:
            return AlertTriangle;
    }
};

export function ServicesStatusWidget({ config, onConfigChange, isEditMode, registerConfigHandler }: ServicesStatusWidgetProps) {
    const [services, setServices] = useState<ServicesData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showConfigModal, setShowConfigModal] = useState(false);
    const [tempSelectedServices, setTempSelectedServices] = useState<string[]>([]);

    const widgetPrefs = useContext(WidgetContext);
    const cardColor = widgetPrefs?.cardColor || DEFAULT_CARD_COLOR;
    const borderColor = widgetPrefs?.borderColor || DEFAULT_BORDER_COLOR;

    // Get configuration values
    const selectedServices = useMemo(() => config?.selectedServices || [], [config?.selectedServices]);
    const showAppName = config?.showAppName !== false; // Default to true

    useEffect(() => {
        const fetchServices = async () => {
            try {
                const protocol = window.location.protocol;
                const host = window.location.host;
                const response = await fetch(`${protocol}//${host}/monitoring-api/api/system/stats`);

                if (response.ok) {
                    const data = await response.json();
                    if (data.success && data.data?.services) {
                        setServices(data.data.services);
                        setError(null);
                    }
                }
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } catch (err: any) {
                setError('Failed to load services');
            } finally {
                setLoading(false);
            }
        };

        fetchServices();

        // Refresh every 10 seconds
        const interval = setInterval(fetchServices, 10000);
        return () => clearInterval(interval);
    }, []);

    // Filter services based on config
    const filteredServices = useMemo(() => {
        if (!services) return {};
        if (selectedServices.length === 0) return services; // Show all if no filter

        return Object.fromEntries(
            Object.entries(services).filter(([key]) => selectedServices.includes(key))
        );
    }, [services, selectedServices]);

    // Open config modal
    const handleOpenConfig = useCallback(() => {
        setTempSelectedServices(selectedServices.length > 0 ? selectedServices : Object.keys(services || {}));
        setShowConfigModal(true);
    }, [selectedServices, services]);

    // Register the config handler for header button
    useEffect(() => {
        if (registerConfigHandler) {
            registerConfigHandler(handleOpenConfig);
        }
    }, [registerConfigHandler, handleOpenConfig]);

    // Save config
    const handleSaveConfig = () => {
        if (onConfigChange) {
            onConfigChange({
                ...config,
                selectedServices: tempSelectedServices,
                showAppName
            });
        }
        setShowConfigModal(false);
    };

    // Toggle service selection
    const toggleService = (key: string) => {
        setTempSelectedServices(prev =>
            prev.includes(key) ? prev.filter(s => s !== key) : [...prev, key]
        );
    };

    if (loading) {
        return (
            <div className="h-full flex items-center justify-center">
                <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    if (error || !services) {
        return (
            <div className="h-full flex items-center justify-center text-muted-foreground">
                <span className="text-sm">{error || 'No data available'}</span>
            </div>
        );
    }

    return (
        <div className="h-full flex flex-col">
            {/* Services List */}
            <div className="flex-1 overflow-auto">
                <div className="space-y-2">
                    {Object.entries(filteredServices).map(([key, service]) => {
                        const Icon = getStatusIcon(service.status);
                        const friendlyName = SERVICE_NAMES[key] || key.charAt(0).toUpperCase() + key.slice(1);

                        return (
                            <div
                                key={key}
                                className="flex items-center justify-between p-2 rounded-lg transition-colors"
                                style={{
                                    backgroundColor: cardColor,
                                    border: `1px solid ${borderColor}`
                                }}
                            >
                                <div className="flex items-center gap-2">
                                    <div className={`p-1.5 rounded-full ${getStatusColor(service.status)}`}>
                                        <Icon className="w-3.5 h-3.5" />
                                    </div>
                                    <div className="flex flex-col">
                                        <span className="text-sm font-medium text-foreground">
                                            {friendlyName}
                                            {showAppName && key !== friendlyName.toLowerCase().replace(/\s/g, '') && (
                                                <span className="text-muted-foreground font-normal"> ({key})</span>
                                            )}
                                        </span>
                                    </div>
                                </div>
                                <span className="text-xs text-muted-foreground">
                                    {service.version && service.version !== 'N/A' ? `v${service.version}` : ''}
                                </span>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Config Modal */}
            {showConfigModal && (
                <div className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center" onClick={() => setShowConfigModal(false)}>
                    <div
                        className="bg-card border border-border rounded-lg shadow-xl p-4 w-80 max-h-[80vh] overflow-auto"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h3 className="text-sm font-semibold text-foreground mb-3">Select Services to Monitor</h3>

                        <div className="space-y-2 mb-4">
                            {Object.keys(services).map((key) => {
                                const isSelected = tempSelectedServices.includes(key);
                                const friendlyName = SERVICE_NAMES[key] || key;

                                return (
                                    <label
                                        key={key}
                                        className="flex items-center gap-2 p-2 rounded hover:bg-accent/50 cursor-pointer"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={() => toggleService(key)}
                                            className="w-4 h-4 rounded border-border"
                                        />
                                        <span className="text-sm text-foreground">
                                            {friendlyName}
                                            <span className="text-muted-foreground"> ({key})</span>
                                        </span>
                                    </label>
                                );
                            })}
                        </div>

                        <div className="flex justify-end gap-2">
                            <button
                                onClick={() => setShowConfigModal(false)}
                                className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSaveConfig}
                                className="px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded hover:bg-primary/90"
                            >
                                Save
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
